// This is a custom time field where it behaves like a text field
// User can still choose to input via the time picker icon

import { AccessTimeFilledOutlined } from "@mui/icons-material";
import { SxProps, TextField, FormControlProps, IconButton, InputAdornment, InputLabelProps } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import React, { useState, useRef } from "react";
import { IMaskInput, IMask } from "react-imask";
import dayjs from "dayjs";

type Size = FormControlProps["size"];
type Margin = FormControlProps["margin"];

type TimeFieldProps = {
  className?: string;
  label?: string;
  value: string;
  placeholder?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  size?: Size;
  margin?: Margin;
  disabled?: boolean;
  required?: boolean;
  InputLabelProps?: Partial<InputLabelProps>;
  onChange: (value: string) => void;
  onBlur?: () => void;
  sx?: SxProps;
  maskLazy?: boolean;
};

interface TimeMaskInputProps {
  onChange: (event: { target: { name?: string; value: string } }) => void;
  name?: string;
  lazy?: boolean;
  [key: string]: any;
}

const TimeMaskInput = React.forwardRef<HTMLInputElement, TimeMaskInputProps>((props, ref) => {
  const { onChange, lazy, ...other } = props;

  // single time block definition
  const singleTimeBlocks = {
    hh: {
      mask: IMask.MaskedRange,
      placeholderChar: "h",
      from: 0,
      to: 23,
      maxLength: 2,
    },
    mm: {
      mask: IMask.MaskedRange,
      placeholderChar: "m",
      from: 0,
      to: 59,
      maxLength: 2,
    },
  };

  // definition for a single time mask
  const singleTimeMask = {
    mask: "hh:`mm", // "`" symbol prevents the ":" to shift back when deleting
    blocks: singleTimeBlocks,
  };

  return (
    <IMaskInput
      {...other}
      mask={singleTimeMask.mask}
      blocks={singleTimeMask.blocks}
      inputRef={ref}
      onAccept={(value: any) => {
        if (onChange) {
          onChange({ target: { name: props.name, value } });
        }
      }}
      lazy={lazy}
      autofix={false}
    />
  );
});

const MobiliTime: React.FC<TimeFieldProps> = (props) => {
  const { className, label, placeholder, id, error, helperText, size, margin, sx, disabled, required, InputLabelProps, maskLazy = false } = props;

  const [openTimePicker, setOpenTimePicker] = useState(false);
  const textFieldRef = useRef<HTMLDivElement>(null);

  // Format value for display
  const displayValue = props.value || "";

  // If maskLazy is true, IMask placeholder is always visible
  // If maskLazy is false, use user-specified or default placeholder
  const finalPlaceholder = maskLazy ? undefined : (placeholder || "hh:mm");

  const handleMaskChange = (event: { target: { name?: string; value: string } }) => {
    const inputValue = event.target.value;

    props.onChange(inputValue);
  };

  const onAdornmentClick = () => {
    setOpenTimePicker(true);
  };

  const parseTimeStringToDate = (timeString: string): Date => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return dayjs().hour(hours || 0).minute(minutes || 0).toDate();
  };

  const formatTimeFromDate = (date: Date): string => {
    return dayjs(date).format('HH:mm');
  };

  return (
    <>
      <TextField
        ref={textFieldRef}
        type="text"
        className={className}
        label={label}
        placeholder={finalPlaceholder}
        id={id}
        value={displayValue}
        onChange={handleMaskChange}
        error={error}
        helperText={helperText}
        size={size}
        margin={margin}
        disabled={disabled}
        required={required}
        onBlur={props.onBlur}
        InputLabelProps={InputLabelProps}
        sx={{
          width: 85,
          ".MuiInputBase-root": {
            height: 35,
            padding: "5px",
            margin: "0px",
            fontSize: "15px",
          },
          ".MuiInputBase-input": {
            padding: "0px",
            margin: "0px",
          },
          ...sx,
        }}
        InputProps={{
          inputComponent: TimeMaskInput,
          inputProps: {
            lazy: !maskLazy,
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton sx={{ margin: 0, padding: 0 }} onClick={onAdornmentClick} disabled={disabled}>
                <AccessTimeFilledOutlined sx={{ fontSize: 18, margin: 0, padding: 0 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TimePicker
        open={openTimePicker}
        onClose={() => setOpenTimePicker(false)}
        value={props.value ? parseTimeStringToDate(props.value) : null}
        onChange={(newValue) => {
          const formatted = newValue ? formatTimeFromDate(newValue) : "";
          props.onChange(formatted);
        }}
        closeOnSelect={false}
        slotProps={{
          textField: {
            sx: { display: "none" },
          },
          popper: {
            anchorEl: textFieldRef.current,
            placement: "bottom-start",
          },
          actionBar: {
            actions: ["accept"],
          },
        }}
        ampm={false}
      />
    </>
  );
};

export default MobiliTime;
